import clsx from 'clsx';
import { useCallback, useContext, useMemo } from 'react';

import {
  WidgetFixedRowLayoutConfiguration,
  WidgetFixedRowLayoutConfigurationDefaults,
  WidgetItemAspectRatioDefault,
  WidgetLayoutArrowConfiguration,
} from '@vestico/model';

import { Carousel, GlobalsContext, RenderArrowProps } from '@vestico/shared';

import { getIsRTL } from '@vestico/i18n';

import { CarouselItem } from '../common/CarouselItem';
import { useCarouselStyles } from '../hooks/useCarouselStyles';
import { useInViewVideo } from '../hooks/useInViewVideo';
import { usePreviewTexts } from '../hooks/usePreviewTexts';
import { BaseLayoutProps } from '../model/baseLayoutProps';
import style from './FixedRowLayout.module.css';
import { FixedRowLayoutArrow } from './FixedRowLayoutArrow';
import { LayoutLoadMoreHandler } from './LayoutLoadMoreHandler';

export interface FixedRowLayoutProps extends BaseLayoutProps {
  layout: WidgetFixedRowLayoutConfiguration;
}

export const FixedRowLayout = ({ layout, responses, onResponseClick, loading, loadMore, hasMore }: FixedRowLayoutProps) => {
  const { showLikes, itemBorderRadius } = layout;
  const aspectRatio = useMemo(() => layout.aspectRatio ?? WidgetItemAspectRatioDefault, [layout.aspectRatio]);
  const itemWidth = useMemo(() => layout.itemWidth ?? WidgetFixedRowLayoutConfigurationDefaults.itemWidth, [layout.itemWidth]);
  const itemPadding = useMemo(() => layout.itemPadding ?? 12, [layout.itemPadding]);

  const { useMobileLayout } = useContext(GlobalsContext);

  // const [currentCarouselIdx, setCurrentCarouselIdx] = useState(0);
  const responsePreviewTexts = usePreviewTexts(layout, responses);

  const handleResponseClick = useCallback((index: number) => onResponseClick(index), [onResponseClick]);
  const { firstInViewVideoResponse, updateInView } = useInViewVideo({ layout, responses });
  const onUpdateInView = useCallback((index: number) => (inView: boolean) => updateInView(index, inView), [updateInView]);

  const { styles, borderRadiusStyles } = useCarouselStyles({ aspectRatio, borderRadius: itemBorderRadius, itemSize: itemWidth, customCSS: {} });

  const responseMarkups = useMemo(() => {
    if (responses.length === 0) {
      return [];
    }

    const finalChildren = responses.map((currentResponse, index) => {
      return (
        <CarouselItem
          key={currentResponse.postDocumentID}
          index={index}
          response={currentResponse}
          onClick={handleResponseClick}
          previewText={responsePreviewTexts[index]}
          showLikes={showLikes}
          aspectRatio={aspectRatio}
          updateInView={onUpdateInView(index)}
          playVideo={currentResponse === firstInViewVideoResponse}
          borderRadius={borderRadiusStyles}
        />
      );
    });
    const loadMoreHandler = hasMore ? <LayoutLoadMoreHandler loading={loading} loadMore={loadMore} isItem /> : null;
    return [...finalChildren, loadMoreHandler];
  }, [
    responses,
    hasMore,
    loading,
    loadMore,
    handleResponseClick,
    responsePreviewTexts,
    showLikes,
    aspectRatio,
    onUpdateInView,
    firstInViewVideoResponse,
    borderRadiusStyles,
  ]);

  // const onDidAnimateToIndex = useCallback((index: number) => setCurrentCarouselIdx(index), []);
  const isRTL = getIsRTL();

  const arrowConfiguration: WidgetLayoutArrowConfiguration = useMemo(() => {
    const currentArrowConfig = layout.arrowConfiguration;
    const enabled = currentArrowConfig?.enabled || (!currentArrowConfig && !useMobileLayout);
    return {
      enabled,
      round: !currentArrowConfig || currentArrowConfig?.round,
      size: currentArrowConfig?.buttonSize,
      arrowSize: currentArrowConfig?.arrowSize,
    };
  }, [layout.arrowConfiguration, useMobileLayout]);

  const disableScrolling = useMemo(() => arrowConfiguration.enabled, [arrowConfiguration.enabled]);

  const renderArrow = useCallback(
    ({ type, onClick, isEdge }: RenderArrowProps) =>
      arrowConfiguration.enabled ? (
        <FixedRowLayoutArrow
          type={type}
          onClick={onClick}
          hidden={isEdge}
          isRTL={isRTL}
          round={arrowConfiguration.round}
          size={arrowConfiguration.buttonSize}
          arrowSize={arrowConfiguration.arrowSize}
        />
      ) : null,
    [isRTL, arrowConfiguration]
  );

  const carousel = (
    <Carousel
      direction="horizontal"
      gap={itemPadding}
      itemLength={itemWidth}
      isRTL={isRTL}
      renderArrow={renderArrow}
      disableScrolling={disableScrolling}
    >
      {responseMarkups}
    </Carousel>
  );

  return (
    <div className={clsx(style.root)} style={styles}>
      {carousel}
    </div>
  );
};
